import React from "react";
import { EditOutlined } from "@mui/icons-material";
import { Button, Divider } from "@mui/material";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EnableDisableButton from "components/Shared/EnableDisableButton";
import LegBasicDetail from "./LegComponents/LegBasicDetail";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import {
  LegDetailContext,
  useLegDetailContext,
} from "contexts/LegDetailContext";
import {
  useGetLegDetailQuery,
  useUpdateLegMutation,
} from "store/services/legApi";
import CustomTabs from "components/Shared/CustomTabs";
import CustomTabPanel from "components/Shared/CustomTabPanel";
import LegNotesTab from "./LegComponents/LegNotesTab";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";

const LegDetailScreen = () => {
  const { id } = useParams();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "notes";

  const breadcrumb = [
    { title: "Leg", path: "/app/crm/leg/list" },
    { title: "Leg Detail" },
  ];

  // RTK Query to fetch Leg details
  const {
    data: legDetail,
    isLoading: loadingLeg,
    error,
  } = useGetLegDetailQuery(id);

  const legDetailTab = [
    { label: "Notes", value: "notes", badgeCount: legDetail?.notes?.length },
  ];
  return (
    <LegDetailContext.Provider value={{ legDetail, loadingLeg }}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumb}
        isBusy={loadingLeg}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <LegBasicDetail />
          <Divider />

          <CustomTabs tabs={legDetailTab} activeTab={activeTab} />
          {!loadingLeg ? (
            <>
              <CustomTabPanel activeTab={activeTab} value="notes">
                <LegNotesTab />
              </CustomTabPanel>
            </>
          ) : (
            <RowsSkeleton />
          )}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </LegDetailContext.Provider>
  );
};

export default LegDetailScreen;

const PageActions = () => {
  const { legDetail, loadingLeg } = useLegDetailContext();

  // RTK Query to update Leg status
  const [updateLeg, { isLoading: updatingLeg }] = useUpdateLegMutation();

  // Toggle status method
  const handleToggleLegStatus = async () => {
    try {
      await updateLeg({
        _id: legDetail._id,
        isActive: !legDetail.isActive,
      }).unwrap();
      toast.info(`Leg ${legDetail.isActive ? "Disabled" : "Enabled"}!`);
    } catch (error) {
      toast.error(error?.data?.message ?? "Could Not Update Status");
    }
  };

  return (
    <div className="flex space-x-4">
      <EnableDisableButton
        isActive={legDetail?.isActive}
        loading={loadingLeg || updatingLeg}
        onClick={handleToggleLegStatus}
      />
      <Link to={`/app/crm/leg/${legDetail?._id}/edit`}>
        <Button startIcon={<EditOutlined />} variant="contained">
          Edit
        </Button>
      </Link>
    </div>
  );
};
